import type { RouteEntry } from '../types';

// ═══════════════════════════════════════════════════════════════════════════
// AUDIT WORKBOOK (flagship)
// ═══════════════════════════════════════════════════════════════════════════

export const AUDIT_WORKBOOK_ROUTES: RouteEntry[] = [
  // ── Overview ──
  {
    path: 'dashboard',
    component: 'DashboardPage',
    label: 'Dashboard',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Overview',
    navIcon: 'Dashboard',
  },

  // ── Data Entry (editable) ──
  // Nav splits by role: company_user items first (navOrder 1–7), then consultant_user (8–15)
  {
    path: 'premiums',
    component: 'PremiumsPage',
    label: 'Premiums',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'AttachMoney',
    navRoles: ['company_user'],
    navOrder: 1,
  },
  {
    path: 'claims',
    component: 'ClaimsPage',
    label: 'Claims',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'Receipt',
    navRoles: ['company_user'],
    navOrder: 2,
  },
  {
    path: 'yield-curves',
    component: 'YieldCurvePage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    hideFromNav: true,
  },
  {
    path: 'reinsurance',
    component: 'ReinsurancePage',
    label: 'Reinsurance',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'CompareArrows',
    navRoles: ['company_user'],
    navOrder: 3,
  },
  {
    path: 'commissions',
    component: 'CommissionsPage',
    label: 'Commissions',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'AttachMoney',
    navRoles: ['company_user'],
    navOrder: 4,
  },
  {
    path: 'expenses',
    component: 'ExpensesPage',
    label: 'Mgmt Expenses',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'AccountBalance',
    navRoles: ['company_user'],
    navOrder: 5,
  },
  {
    path: 'expense-allocations',
    component: 'ExpenseAllocationsPage',
    label: 'Expense Allocations',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'PieChart',
    navRoles: ['company_user'],
    navOrder: 6,
  },
  {
    path: 'ifrs4-balance-sheet',
    component: 'IFRS4BalanceSheetPage',
    label: 'IFRS 4 Balance Sheet',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'HistoryEdu',
    navRoles: ['company_user'],
    ifrs4Only: true,
    navOrder: 7,
  },
  // consultant_user data entry items
  {
    path: 'upr',
    component: 'UPRPage',
    label: 'UPR',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'TrendingDown',
    navRoles: ['consultant_user'],
    navOrder: 8,
  },
  {
    path: 'dac',
    component: 'DACPage',
    label: 'DAC',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'MoneyOff',
    navRoles: ['consultant_user'],
    navOrder: 9,
  },
  {
    path: 'risk-adjustment',
    component: 'RiskAdjustmentPage',
    label: 'Risk Adjustment',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'Security',
    navRoles: ['consultant_user'],
    navOrder: 10,
  },
  {
    path: 'retention-rates',
    component: 'RetentionRatesPage',
    label: 'Retention Rates',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'Percent',
    navRoles: ['consultant_user'],
    navOrder: 11,
  },
  // consultant_user also sees Expense Allocations (duplicate nav entry, same path)
  {
    path: 'expense-allocations',
    component: 'ExpenseAllocationsPage',
    label: 'Expense Allocations',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'PieChart',
    navRoles: ['consultant_user'],
    navOrder: 12,
    hideFromNav: false, // nav-only duplicate — route already defined above
  },
  {
    path: 'triangles',
    component: 'TrianglesPage',
    label: 'Triangles',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'GridOn',
    navRoles: ['consultant_user'],
    navOrder: 13,
  },
  {
    path: 'gmm-data-entry',
    component: 'GMMDataEntryPage',
    label: 'GMM Data Entry',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'AccountTree',
    navRoles: ['consultant_user'],
    navOrder: 14,
  },
  {
    path: 'life-assumptions',
    component: 'LifeAssumptionsPage',
    label: 'Life Assumptions',
    allowedRoles: [], // no ProtectedRoute wrapper — all authenticated
    functionality: 'audit_workbook',
    navSection: 'Data Entry',
    navIcon: 'GridOn',
    navRoles: ['consultant_user'],
    navOrder: 15,
  },
  {
    path: 'revenue-account',
    component: 'RevenueAccountPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    hideFromNav: true,
  },
  {
    path: 'balance-sheet-entry',
    component: 'BalanceSheetEntryPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    hideFromNav: true,
  },

  // ── Data Entry (bulk) ──
  {
    path: 'bulk-upload',
    component: 'BulkUploadPage',
    label: 'Bulk CSV Upload',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Import / Export',
    navIcon: 'SwapVert',
    navRoles: ['company_user', 'consultant_user'],
  },

  // ── Data Review (auditor read-only views) ──
  {
    path: 'provided/premiums',
    component: 'PremiumsPage',
    label: 'Premiums',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    navCollapsible: true,
    readOnly: true,
    navOrder: 1,
  },
  {
    path: 'provided/claims',
    component: 'ClaimsPage',
    label: 'Claims',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 2,
  },
  {
    path: 'provided/reinsurance',
    component: 'ReinsurancePage',
    label: 'Reinsurance',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 3,
  },
  {
    path: 'provided/commissions',
    component: 'CommissionsPage',
    label: 'Commissions',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 4,
  },
  {
    path: 'provided/expenses',
    component: 'ExpensesPage',
    label: 'Mgmt Expenses',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 5,
  },
  {
    path: 'provided/expense-allocations',
    component: 'ExpenseAllocationsPage',
    label: 'Expense Allocations',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 6,
  },
  {
    path: 'provided/upr',
    component: 'UPRPage',
    label: 'UPR',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 7,
  },
  {
    path: 'provided/dac',
    component: 'DACPage',
    label: 'DAC',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 8,
  },
  {
    path: 'provided/risk-adjustment',
    component: 'RiskAdjustmentPage',
    label: 'Risk Adjustment',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 9,
  },
  {
    path: 'provided/retention-rates',
    component: 'RetentionRatesPage',
    label: 'Retention Rates',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 10,
  },
  {
    path: 'provided/triangles',
    component: 'TrianglesPage',
    label: 'Triangles',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Review',
    navIcon: 'Visibility',
    navRoles: ['auditor'],
    readOnly: true,
    navOrder: 11,
  },

  // ── Data Provided (company_user sees consultant data; consultant_user sees analyst data) ──
  // company_user sees consultant-provided data as read-only
  {
    path: 'provided/upr',
    component: 'UPRPage',
    label: 'UPR',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['company_user'],
    readOnly: true,
    navOrder: 1,
  },
  {
    path: 'provided/dac',
    component: 'DACPage',
    label: 'DAC',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['company_user'],
    readOnly: true,
    navOrder: 2,
  },
  {
    path: 'provided/risk-adjustment',
    component: 'RiskAdjustmentPage',
    label: 'Risk Adjustment',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['company_user'],
    readOnly: true,
    navOrder: 3,
  },
  {
    path: 'provided/retention-rates',
    component: 'RetentionRatesPage',
    label: 'Retention Rates',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['company_user'],
    readOnly: true,
    navOrder: 4,
  },
  {
    path: 'provided/triangles',
    component: 'TrianglesPage',
    label: 'Triangles',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['company_user'],
    readOnly: true,
    navOrder: 5,
  },
  // consultant_user sees analyst-provided data as read-only
  {
    path: 'provided/premiums',
    component: 'PremiumsPage',
    label: 'Premiums',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 6,
  },
  {
    path: 'provided/claims',
    component: 'ClaimsPage',
    label: 'Claims',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 7,
  },
  {
    path: 'provided/reinsurance',
    component: 'ReinsurancePage',
    label: 'Reinsurance',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 8,
  },
  {
    path: 'provided/commissions',
    component: 'CommissionsPage',
    label: 'Commissions',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 9,
  },
  {
    path: 'provided/expenses',
    component: 'ExpensesPage',
    label: 'Mgmt Expenses',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 10,
  },
  {
    path: 'provided/expense-allocations',
    component: 'ExpenseAllocationsPage',
    label: 'Expense Allocations',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    navOrder: 11,
  },
  {
    path: 'provided/ifrs4-balance-sheet',
    component: 'IFRS4BalanceSheetPage',
    label: 'IFRS 4 Balance Sheet',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Data Provided',
    navIcon: 'Visibility',
    navRoles: ['consultant_user'],
    readOnly: true,
    ifrs4Only: true,
    navOrder: 12,
  },

  // ── Hidden provided routes (no nav) ──
  {
    path: 'provided/revenue-account',
    component: 'RevenueAccountPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    hideFromNav: true,
    readOnly: true,
  },
  {
    path: 'provided/balance-sheet-entry',
    component: 'BalanceSheetEntryPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    hideFromNav: true,
    readOnly: true,
  },

  // ── Analysis ──
  {
    path: 'calculations',
    component: 'CalculationsPage',
    label: 'Calculations',
    allowedRoles: ['super_admin', 'admin', 'consultant_user', 'company_user', 'auditor', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'Calculate',
    navRoles: ['consultant_user'],
    navOrder: 1,
  },
  {
    path: 'life-calculations',
    component: 'LifeCalculationPage',
    label: 'Life Calculations',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'Calculate',
    navRoles: ['consultant_user'],
    navOrder: 2,
  },
  {
    path: 'approvals',
    component: 'ApprovalsPage',
    label: 'Approvals',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'FactCheck',
    navRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
    navOrder: 3,
  },
  // Audit section: auditor sees Approvals as "Audit Trail"
  {
    path: 'approvals',
    component: 'ApprovalsPage',
    label: 'Audit Trail',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Audit',
    navIcon: 'FactCheck',
    navRoles: ['auditor'],
  },
  {
    path: 'disclosures',
    component: 'DisclosuresPage',
    label: 'Disclosures',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'Article',
  },
  {
    path: 'life-disclosures',
    component: 'LifeDisclosuresPage',
    label: 'Life Disclosures',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'Article',
  },
  {
    path: 'life-contract-groups',
    component: 'LifeContractGroupsPage',
    label: 'Life Contract Groups',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'AccountTree',
    navRoles: ['company_user', 'consultant_user', 'auditor'],
  },
  {
    path: 'life-reconciliation',
    component: 'LifeReconciliationPage',
    label: 'Life Reconciliation',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'TableChart',
    // MISMATCH: nav has no roles (all) but route restricts to 5 roles
  },
  {
    path: 'life-sensitivity',
    component: 'LifeSensitivityPage',
    label: 'Life Sensitivity',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'ShowChart',
    // MISMATCH: nav has no roles (all) but route restricts to 5 roles
  },
  {
    path: 'life-asset-share',
    component: 'LifeAssetSharePage',
    label: 'Asset Share (VFA)',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'PieChart',
    navRoles: ['company_user', 'consultant_user', 'auditor'],
  },
  {
    path: 'job-status',
    component: 'JobStatusPage',
    label: 'Job Status',
    allowedRoles: ['super_admin', 'admin', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'MonitorHeart',
    navRoles: ['super_admin', 'admin', 'consultant_user'],
  },
  {
    path: 'financial-statements',
    component: 'FinancialStatementsPage',
    label: 'Financial Statements',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'AccountBalanceWallet',
    // MISMATCH: nav has no roles (all) but route restricts to 5 roles
  },
  {
    path: 'sensitivity',
    component: 'SensitivityPage',
    label: 'Sensitivity Analysis',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'ShowChart',
    navRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
  },
  {
    path: 'risk-disclosures',
    component: 'RiskDisclosuresPage',
    label: 'Risk Disclosures',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'Shield',
    // Nav has no roles (all). Route restricts to 5 roles
  },
  {
    path: 'gmm-disclosures',
    component: 'GMMReconciliationPage',
    label: 'GMM Disclosures',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator', 'executive'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'TableChart',
    navRoles: ['company_user', 'consultant_user', 'auditor', 'executive'],
  },
  {
    path: 'ifrs4-conversion',
    component: 'IFRS4ConversionPage',
    label: 'IFRS 4 Conversion',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Analysis',
    navIcon: 'CompareArrows',
    navRoles: ['company_user', 'consultant_user', 'auditor'],
    ifrs4Only: true,
  },

  // ── Quality Assurance ──
  {
    path: 'model-validation',
    component: 'ModelValidationPage',
    label: 'Model Validation',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    navSection: 'Quality Assurance',
    navIcon: 'VerifiedUser',
    navRoles: ['company_user', 'consultant_user', 'auditor'],
    navOrder: 1,
  },

  // ── Import / Export ──
  {
    path: 'import-export',
    component: 'ImportExportPage',
    label: 'Excel Import/Export',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    navSection: 'Import / Export',
    navIcon: 'SwapVert',
    navRoles: ['company_user', 'consultant_user'],
  },

  // ── Reporting ──
  {
    path: 'xbrl',
    component: 'XBRLPage',
    label: 'XBRL Reporting',
    allowedRoles: ['company_user', 'consultant_user', 'auditor', 'regulator'],
    functionality: 'audit_workbook',
    navSection: 'Reporting',
    navIcon: 'DataObject',
    navRoles: ['company_user', 'consultant_user', 'auditor', 'regulator'],
  },

  // ── Misc audit_workbook routes without nav items ──
  {
    path: 'completeness',
    component: 'CompletenessPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user', 'auditor'],
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'claims-development',
    component: 'ClaimsDevelopmentPage',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'narrative-disclosures',
    component: 'NarrativeDisclosuresPage',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'period-management',
    component: 'PeriodManagementPage',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'reserve-results',
    component: 'ReserveResultsPage',
    label: 'Reserve Results',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: has nav item in actuarial_model but NOT in audit_workbook. Route has no role guard
  },
  {
    path: 'lt-revenue-account',
    component: 'LTRevenueAccountPage',
    label: '',
    allowedRoles: ['company_user', 'consultant_user'],
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'vfa-data-entry',
    component: 'VFADataEntryPage',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
  {
    path: 'lapse-tables',
    component: 'LapseTablesPage',
    label: '',
    allowedRoles: [], // no ProtectedRoute wrapper
    functionality: 'audit_workbook',
    hideFromNav: true,
    // MISMATCH: route exists but no nav item
  },
];
